import React, { FC, useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { SessionType } from "@/features/training/model/Training/TrainingDrill";
import {
	TrainingSessionStatus,
	TrainingSessionSummary,
} from "@/features/training/model/Training/TrainingSession";
import { TrainingType } from "@/features/training/pages/Archive/panel-left/ArchivePanelLeft";
import { TableContainer } from "@/shared/ui";
import {
	Box,
	Checkbox,
	Chip,
	LinearProgress,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import {
	DataGrid,
	GridColDef,
	GridRowParams,
	GridToolbar,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
	useFetchCourseSessions,
	useFetchDrillSessions,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);

interface ArchivedSessionsDataGridProps {
	startDate?: Date;
	endDate?: Date;
	trainingType: TrainingType;
}

type ArchivedSessionRow = {
	Id: number;
	Name: string;
	Date: Date;
	Type: SessionType | "Course";
	Standin?: boolean;
	CourseId?: number;
	DrillId?: number;
};

export const ArchivedSessionsDataGrid: FC<ArchivedSessionsDataGridProps> = (
	props,
) => {
	const translation: Language | null = useContext(TranslationContext);
	const navigate = useNavigate();
	const theme = useTheme();
	const { timezone } = useTime();

	const [sessions, setSessions] = useState<ArchivedSessionRow[]>([]);
	const [paginationModel, setPaginationModel] = useState({
		pageSize: 10,
		page: 0,
	});
	const periodLabel = `${props.startDate ? dayjs(props.startDate).format("DD/MM/YY") : "—"} → ${
		props.endDate ? dayjs(props.endDate).format("DD/MM/YY") : "—"
	}`;

	const queryOptions = {
		StartDate: props.startDate
			? dayjs(props.startDate).format("YYYY.MM.DD")
			: undefined,
		EndDate: props.endDate
			? dayjs(props.endDate).format("YYYY.MM.DD")
			: undefined,
		Status: TrainingSessionStatus.Archived,
	};

	const isCourseSessionsActive =
		props.trainingType === TrainingType.All ||
		props.trainingType === TrainingType.Courses;
	const isDrillSessionsActive =
		props.trainingType === TrainingType.All ||
		props.trainingType === TrainingType.Exercises ||
		props.trainingType === TrainingType.Events;

	const { isLoading: isLoadingCourses, data: courseSessions } =
		useFetchCourseSessions(queryOptions, isCourseSessionsActive);
	const { isLoading: isLoadingDrills, data: drillSessions } =
		useFetchDrillSessions(queryOptions, isDrillSessionsActive);

	useEffect(() => {
		let merged: ArchivedSessionRow[] = [];

		if (isCourseSessionsActive && courseSessions) {
			const asCourse: ArchivedSessionRow[] = courseSessions.map(
				(session: TrainingSessionSummary) => ({
					Id: session.Id,
					Name: session.Name,
					Date: session.Date,
					CourseId: session.CourseId,
					Type: "Course",
				}),
			);
			merged = merged.concat(asCourse);
		}

		if (props.trainingType === TrainingType.All && drillSessions) {
			merged = merged.concat(drillSessions as ArchivedSessionRow[]);
		} else if (props.trainingType === TrainingType.Exercises && drillSessions) {
			merged = merged.concat(
				(drillSessions as ArchivedSessionRow[]).filter(
					(session) => session.Type === SessionType.Exercise,
				),
			);
		} else if (props.trainingType === TrainingType.Events && drillSessions) {
			merged = merged.concat(
				(drillSessions as ArchivedSessionRow[]).filter(
					(session) => session.Type === SessionType.Event,
				),
			);
		}

		setSessions(merged);
	}, [
		props.trainingType,
		courseSessions,
		drillSessions,
		isCourseSessionsActive,
	]);

	const typeToChip = (type: ArchivedSessionRow["Type"]) => {
		if (type === "Course") {
			return (
				<Chip
					size="small"
					label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Archive.PanelLeft.ArchivedSessionsDataGrid.L139C12.Course", "Course")}
					color="primary"
					variant="outlined"
					sx={{ fontWeight: 600, borderRadius: 2 }}
				/>
			);
		}
		if (type === SessionType.Exercise || type === 0) {
			return (
				<Chip
					size="small"
					label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Archive.PanelLeft.ArchivedSessionsDataGrid.L150C12.Exercise", "Exercise")}
					color="info"
					variant="outlined"
					sx={{ fontWeight: 600, borderRadius: 2 }}
				/>
			);
		}
		if (type === SessionType.Event || type === 1) {
			return (
				<Chip
					size="small"
					label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Archive.PanelLeft.ArchivedSessionsDataGrid.L161C12.Event", "Event")}
					color="success"
					variant="outlined"
					sx={{ fontWeight: 600, borderRadius: 2 }}
				/>
			);
		}
		return <Chip size="small" label={String(type)} variant="outlined" sx={{ borderRadius: 2 }} />;
	};

	const columns: GridColDef<ArchivedSessionRow>[] = [
		{
			field: "Name",
			renderHeader: () => (
				<Typography component="span" sx={{ fontWeight: 700 }}>
					<Translate String="Training.Archive.SessionName">Name</Translate>
				</Typography>
			),
			flex: 1.2,
			minWidth: 160,
			sortable: true,
		},
		{
			field: "Type",
			renderHeader: () => (
				<Typography component="span" sx={{ fontWeight: 700 }}>
					<Translate String="Training.Archive.SessionType">Type</Translate>
				</Typography>
			),
			flex: 0.5,
			minWidth: 100,
			sortable: true,
			renderCell: ({ row }) => typeToChip(row.Type),
		},
		{
			field: "Date",
			renderHeader: () => (
				<Typography component="span" sx={{ fontWeight: 700 }}>
					<Translate String="Training.Archive.SessionDate">Date</Translate>
				</Typography>
			),
			flex: 0.7,
			minWidth: 100,
			sortable: true,
			renderCell: ({ row }) =>
				dayjs.utc(row.Date).tz(timezone).format("DD/MM/YY"),
		},
		{
			field: "Standin",
			renderHeader: () => (
				<Typography component="span" sx={{ fontWeight: 700 }}>
					<Translate String="Training.DrillSession.Standin">Stand in</Translate>
				</Typography>
			),
			flex: 0.4,
			minWidth: 90,
			sortable: true,
			align: "center",
			headerAlign: "center",
			renderCell: ({ row }) =>
				row.Standin ? (
					<Chip
						size="small"
						label={
							<Translate String="Archive.DataGrid.Overridden.Yes">
								Yes
							</Translate>
						}
						color="warning"
						variant="outlined"
						sx={{ borderRadius: 2 }}
					/>
				) : (
					<Chip
						size="small"
						label={
							<Translate String="Archive.DataGrid.Overridden.No">No</Translate>
						}
						variant="outlined"
						sx={{ borderRadius: 2 }}
					/>
				),
		},
	];

	const loading = isLoadingCourses || isLoadingDrills;

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: 240,
					gap: 2,
				}}
			>
				<LinearProgress sx={{ width: "100%" }} />
				<Typography variant="body2" color="text.secondary">
					<Translate String="Training.Archive.LoadingSessions">
						Loading sessions…
					</Translate>
				</Typography>
			</Box>
		);
	}

	if (sessions.length === 0) {
		return (
			<Box
				sx={{
					p: 4,
					borderRadius: 3,
					textAlign: "center",
					border: "1px dashed",
					borderColor: "divider",
					bgcolor: (t) =>
						alpha(
							t.palette.primary.main,
							t.palette.mode === "dark" ? 0.04 : 0.02,
						),
				}}
			>
				<Typography variant="body1" color="text.secondary" fontWeight={600}>
					<Translate String="Training.Archive.NoSessionsFound">
						No sessions found
					</Translate>
				</Typography>
				<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
					{periodLabel}
				</Typography>
			</Box>
		);
	}

	const handleRowClick = (params: GridRowParams<ArchivedSessionRow>) => {
		const state = params.row.CourseId
			? { courseId: params.row.CourseId }
			: { drillId: params.row.DrillId };
		navigate(`/training/archive/${params.id}`, { state });
	};

	const dataGridSx = {
		border: "none",
		bgcolor: "transparent",
		color: "text.primary",
		"& .MuiDataGrid-root": { border: "none" },
		"& .MuiDataGrid-columnHeaders": {
			bgcolor:
				theme.palette.mode === "light"
					? alpha(theme.palette.primary.main, 0.04)
					: alpha(theme.palette.common.white, 0.03),
			borderBottom: `1px solid ${theme.palette.divider}`,
			minHeight: "48px !important",
			"& .MuiDataGrid-columnHeaderTitle": {
				fontWeight: 700,
				fontSize: theme.typography.body2.fontSize,
			},
		},
		"& .MuiDataGrid-cell": {
			borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
		},
		"& .MuiDataGrid-row": {
			bgcolor: "background.paper",
		},
		"& .MuiDataGrid-row:hover": {
			bgcolor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "light" ? 0.06 : 0.12,
			),
			cursor: "pointer",
		},
		"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
			outline: "none",
		},
		"& .MuiDataGrid-cell:focus-visible": {
			boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
		},
		"& .MuiDataGrid-footerContainer": {
			borderTop: `1px solid ${theme.palette.divider}`,
			bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.02) : "grey.50",
		},
		"& .MuiDataGrid-toolbarContainer": {
			borderBottom: `1px solid ${theme.palette.divider}`,
			bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.02) : "grey.50",
			px: 1,
			py: 0.75,
			gap: 1,
		},
		"& .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.divider,
		},
		"& .MuiDataGrid-toolbarContainer .MuiButton-root": {
			borderRadius: 2,
			textTransform: "none",
		},
		"& .MuiDataGrid-toolbarContainer .MuiInputBase-root": {
			borderRadius: 2,
			bgcolor: "background.paper",
		},
	};

	return (
		<Box
			sx={{
				borderRadius: 3,
				overflow: "hidden",
				border: 1,
				borderColor: "divider",
				bgcolor: "background.paper",
			}}
		>
			<Box
				sx={(t) => ({
					px: 2.25,
					py: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.25,
					flexWrap: "wrap",
					borderBottom: 1,
					borderColor: alpha(t.palette.divider, 0.85),
					bgcolor: t.palette.mode === "dark" ? alpha(t.palette.common.white, 0.02) : "grey.50",
				})}
			>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.05,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Auto.Features.Training.Pages.Archive.PanelLeft.ArchivedSessionsDataGrid.L397C7.Archive">Archive</Translate>
					</Typography>
					<Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.05 }}>
						<Translate String="Training.Archive.SessionList">
							Archived Sessions
						</Translate>
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{periodLabel}
					</Typography>
				</Box>
				<Stack
					direction="row"
					spacing={0.75}
					sx={{
						maxWidth: "100%",
						overflowX: "auto",
						whiteSpace: "nowrap",
						flexWrap: "nowrap",
						pb: 0.25,
						"&::-webkit-scrollbar": { height: 6 },
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: alpha(theme.palette.text.primary, 0.18),
							borderRadius: 999,
						},
					}}
				>
					<Chip
						size="small"
						label={
							<Stack direction="row" spacing={0.75} alignItems="center">
								<Typography variant="caption" color="text.secondary" fontWeight={800}>
									<Translate String="Auto.Features.Training.Pages.Archive.PanelLeft.ArchivedSessionsDataGrid.L429C79.Sessions">Sessions</Translate>
								</Typography>
								<Typography variant="caption" fontWeight={950}>
									{sessions.length}
								</Typography>
							</Stack>
						}
						sx={{
							height: 26,
							borderRadius: 2,
							bgcolor: alpha(
								theme.palette.primary.main,
								theme.palette.mode === "dark" ? 0.16 : 0.09,
							),
							"& .MuiChip-label": { px: 1 },
						}}
					/>
					<Chip
						size="small"
						label={
							<Stack direction="row" spacing={0.75} alignItems="center">
								<Typography variant="caption" color="text.secondary" fontWeight={800}>
									<Translate String="Auto.Features.Training.Pages.Archive.PanelLeft.ArchivedSessionsDataGrid.L451C79.Type">Type</Translate>
								</Typography>
								<Typography variant="caption" fontWeight={950}>
									{props.trainingType}
								</Typography>
							</Stack>
						}
						sx={{
							height: 26,
							borderRadius: 2,
							bgcolor: alpha(
								theme.palette.info.main,
								theme.palette.mode === "dark" ? 0.16 : 0.09,
							),
							"& .MuiChip-label": { px: 1 },
						}}
					/>
				</Stack>
			</Box>

			<TableContainer height={420} sx={{ border: "none", bgcolor: "background.paper" }}>
				<DataGrid
					rows={sessions}
					getRowId={(row) => `${row.Id}-${row.Type}`}
					columns={columns}
					rowHeight={44}
					onRowClick={handleRowClick}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					disableColumnSelector
					disableDensitySelector
					disableColumnFilter
					slots={{
						toolbar: GridToolbar,
						booleanCellTrueIcon: () => <Checkbox checked />,
						booleanCellFalseIcon: () => <Checkbox />,
					}}
					slotProps={{
						toolbar: {
							showQuickFilter: true,
							quickFilterProps: { debounceMs: 300 },
							printOptions: { disableToolbarButton: true },
							csvOptions: { disableToolbarButton: true },
						},
					}}
					sx={dataGridSx}
				/>
			</TableContainer>
		</Box>
	);
};

export default ArchivedSessionsDataGrid;
